import { useState, type ReactNode } from "react";
import type { CardPreviewDensity } from "@/components/shapes/card-previews";
import { PreviewBody } from "@/components/shapes/shared/card-preview-parts";
import { OPENED_PART } from "@/components/shapes/shared/object-frame";
import { cn } from "@/lib/cn";
import { ChartBars } from "./chart-bars";
import { ChartLine } from "./chart-line";
import { ChartNumber } from "./chart-number";
import { ChartHeadline } from "./chart-parts";
import {
  chartChangeText,
  formatChartValue,
  type ChartKind,
  type ChartPoint,
} from "./chart-points";

/**
 * A chart, the card and its opened window: its title, then by `kind` the
 * shown value with its unit and change over bars or a line, or one big
 * number. The card shows the latest; opened, a click on a bar or a line's
 * point shows that point, and the insight and `footer` follow.
 */
export function ChartFace({
  kind = "bar",
  title,
  unit,
  insight,
  points,
  density,
  opened = false,
  testIdPrefix,
  empty,
  footer,
}: {
  kind?: ChartKind;
  title: string;
  unit: string;
  /** The opened window's last line. */
  insight?: string;
  points: readonly ChartPoint[];
  density: CardPreviewDensity;
  opened?: boolean;
  /** `chart-card` or `chart-frame`: the prefix of the parts' test ids. */
  testIdPrefix: string;
  /** Shown under the title when there are no points. */
  empty: ReactNode;
  /** The opened window's source and period, under everything else. */
  footer?: ReactNode;
}) {
  const [selected, setSelected] = useState<number | null>(null);
  // A selection past the points an update left falls back to the latest.
  const shown =
    kind !== "number" && selected !== null && selected < points.length
      ? selected
      : points.length - 1;
  const point = points[shown];
  const drawing = {
    title,
    unit,
    points,
    shown,
    selected,
    onSelect: setSelected,
    opened,
    testIdPrefix,
  };
  return (
    <PreviewBody
      density={density}
      cap="h-[15em]"
      className={cn("gap-[0.3em]", opened && OPENED_PART)}
    >
      <p
        className="truncate text-[0.84em] text-card-muted"
        data-testid={`${testIdPrefix}-title`}
        data-frame-title="card"
      >
        {title}
      </p>
      {!point ? (
        empty
      ) : kind === "number" ? (
        <ChartNumber points={points} unit={unit} testIdPrefix={testIdPrefix} />
      ) : (
        <>
          <ChartHeadline
            value={formatChartValue(point.value)}
            unit={unit}
            change={chartChangeText(points, shown)}
            testId={`${testIdPrefix}-value`}
          />
          {kind === "line" ? (
            <ChartLine {...drawing} />
          ) : (
            <ChartBars {...drawing} />
          )}
        </>
      )}
      {opened && point && insight && (
        <p
          className="truncate text-[0.84em] text-card-muted"
          data-testid={`${testIdPrefix}-insight`}
        >
          {insight}
        </p>
      )}
      {opened && footer}
    </PreviewBody>
  );
}
